import React, { useCallback, useEffect, useState } from 'react';

import { InputOnBlur } from '../Input';

import makeString from './makeString';
import makeValidNumber from './makeValidNumber';

type InputOnBlurOnChange = NonNullable<Parameters<typeof InputOnBlur>[0]['onChange']>;
export type NumberProps = Omit<Parameters<typeof InputOnBlur>[0], 'type' | 'suffix' | 'onChange' | 'value'> & {
  value: number | null;
  numberType?: 'integer' | 'float';
  onChange: (val: number | null, event?: Parameters<InputOnBlurOnChange>[1]) => void;
  onBlur?: Parameters<typeof InputOnBlur>[0]['onBlur'];
  suffix?: Parameters<typeof InputOnBlur>[0]['suffix'];
  min?: number;
  max?: number;
};

const NumberInputOnBlur: React.FC<NumberProps> = ({ numberType, onChange, value, onBlur, min, max, ...props }) => {
  const [inputOnBlurValue, setInputOnBlurValue] = useState<number | null>(value ?? null);

  const valueRef = React.useRef(inputOnBlurValue);
  valueRef.current = inputOnBlurValue;

  useEffect(() => {
    setInputOnBlurValue(value ?? null);
  }, [value]);

  const handleChange = useCallback(
    (val: string) => {
      setInputOnBlurValue(makeValidNumber(val, { max, min, numberType }));
    },
    [max, min, numberType],
  );

  const handleBlur = useCallback(
    (asString: string, event: React.FocusEvent<HTMLInputElement> | undefined) => {
      const newValue = makeValidNumber(asString, { max, min, numberType });
      onChange(newValue, event);
      onBlur?.(asString, event);
    },
    [max, min, numberType, onBlur, onChange],
  );

  return (
    <InputOnBlur
      {...props}
      value={makeString(inputOnBlurValue)}
      type='number'
      inputMode='numeric'
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default NumberInputOnBlur;
